/**
 * Shared "recipe opens beside the list" mechanics — extracted from
 * /recipes so /shopping-list's Recipes tab reuses the exact behavior
 * instead of re-deriving it:
 *
 *   - keep the panel mounted through its 200ms close animation so the
 *     width collapse reads smoothly (mirrors /orders kitchen),
 *   - close the open panel on Escape,
 *   - collapse the global sidebar while a panel is open so it has room,
 *   - desktop: a width-transition side column; narrow viewports: a
 *     full-screen overlay.
 *
 * The hook owns state + effects; the components own layout. Fullscreen
 * (the centered Dialog) is owned here too: the panel's "Full screen" chip
 * calls `slot.openFullscreen`, and every host renders
 * `RecipeDetailFullscreenDialog` next to the narrow overlay. Hosting the
 * slot without the dialog used to leave the chip a silent no-op.
 */

import { cn } from '@ordo/ui/cn'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@ordo/ui/dialog'
import { useCallback, useEffect, useState } from 'react'

import { RecipeDetailPanel } from '@/components/recipes/recipe-detail-panel'
import { useChefSidebarCollapse } from '@/stores/chefs/sidebar'

export interface RecipeDetailSlotState {
  /** The component the user has open; null = closed. */
  activeComponentId: string | null
  setActiveComponentId: (id: string | null, variantId?: string | null) => void
  componentVariantId?: string | null
  fullscreenVariantId?: string | null
  /** Stays set through the close animation — render off THIS id. */
  renderedComponentId: string | null
  slotOpen: boolean
  /** Component promoted into the centered "Full screen" Dialog; null = closed. */
  fullscreenComponentId: string | null
  /** Promote a component into the Dialog and close the side panel. */
  openFullscreen: (id: string) => void
  closeFullscreen: () => void
}

export function useRecipeDetailSlot(): RecipeDetailSlotState {
  const [activeComponentId, setComponentId] = useState<string | null>(null)
  const [componentVariantId, setComponentVariantId] = useState<string | null>(null)
  const setActiveComponentId = useCallback((id: string | null, variantId: string | null = null) => {
    setComponentId(id)
    if (id) {
      setComponentVariantId(variantId)
    }
  }, [])
  const slotOpen = activeComponentId !== null

  // Keep the panel mounted through its 200ms close animation so the
  // width collapse reads smoothly (mirrors /orders kitchen).
  const [renderedComponentId, setRenderedComponentId] = useState<string | null>(null)
  if (activeComponentId && activeComponentId !== renderedComponentId) {
    setRenderedComponentId(activeComponentId)
  }
  useEffect(() => {
    if (activeComponentId || !renderedComponentId) {
      return
    }
    const t = window.setTimeout(() => setRenderedComponentId(null), 200)
    return () => window.clearTimeout(t)
  }, [activeComponentId, renderedComponentId])

  // Close the open panel on Escape.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && activeComponentId) {
        setActiveComponentId(null)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [activeComponentId, setActiveComponentId])

  // Collapse the global sidebar while the detail panel is open so the
  // panel has room (mirrors chefs-v2's standalone-shell behavior).
  const setChefSidebarCollapsed = useChefSidebarCollapse((s) => s.setCollapsed)
  useEffect(() => {
    setChefSidebarCollapsed(slotOpen)
    return () => setChefSidebarCollapsed(false)
  }, [slotOpen, setChefSidebarCollapsed])

  const [fullscreenComponentId, setFullscreenComponentId] = useState<string | null>(null)
  const [fullscreenVariantId, setFullscreenVariantId] = useState<string | null>(null)
  const openFullscreen = useCallback(
    (id: string) => {
      setFullscreenVariantId(componentVariantId)
      setFullscreenComponentId(id)
      setActiveComponentId(null)
    },
    [componentVariantId, setActiveComponentId],
  )
  const closeFullscreen = useCallback(() => setFullscreenComponentId(null), [])

  return {
    activeComponentId,
    componentVariantId,
    fullscreenVariantId,
    setActiveComponentId,
    renderedComponentId,
    slotOpen,
    fullscreenComponentId,
    openFullscreen,
    closeFullscreen,
  }
}

/**
 * Desktop side-column slot. Render as the LAST flex child of the
 * list+panel row (`flex min-h-0 flex-1 items-stretch gap-2`). Renders
 * nothing on narrow viewports — pair with `RecipeDetailNarrowOverlay`.
 */
export function RecipeDetailSlot({
  slot,
  isNarrow,
  /** Vertical padding of the panel column — MUST match the sibling list's
   *  top padding or the panel floats below the list's first card. */
  paddingClassName = 'pb-4 pt-5',
}: {
  slot: RecipeDetailSlotState
  isNarrow: boolean
  paddingClassName?: string
}) {
  if (isNarrow) {
    return null
  }
  return (
    <div
      className={cn(
        'relative shrink-0 overflow-hidden transition-[width] duration-200 ease-out',
        slot.slotOpen ? 'w-[clamp(440px,45vw,600px)]' : 'w-0',
      )}>
      {slot.renderedComponentId ? (
        <div className={cn('absolute inset-y-0 left-0 w-[clamp(440px,45vw,600px)]', paddingClassName)}>
          <RecipeDetailPanel
            componentId={slot.renderedComponentId}
            componentVariantId={slot.componentVariantId}
            onClose={() => slot.setActiveComponentId(null)}
            onFullscreen={slot.openFullscreen}
          />
        </div>
      ) : null}
    </div>
  )
}

/** Narrow viewports: opening a recipe takes over the screen. */
export function RecipeDetailNarrowOverlay({ slot, isNarrow }: { slot: RecipeDetailSlotState; isNarrow: boolean }) {
  if (!isNarrow || !slot.activeComponentId) {
    return null
  }
  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-background">
      <RecipeDetailPanel
        componentId={slot.activeComponentId}
        componentVariantId={slot.componentVariantId}
        onClose={() => slot.setActiveComponentId(null)}
      />
    </div>
  )
}

/** Desktop "Full screen" → centered Dialog. Render once per host page. */
export function RecipeDetailFullscreenDialog({ slot }: { slot: RecipeDetailSlotState }) {
  return (
    <Dialog open={slot.fullscreenComponentId !== null} onOpenChange={(next) => (next ? null : slot.closeFullscreen())}>
      <DialogContent hideCloseButton className="p-0 sm:max-w-3xl sm:rounded-2xl sm:h-[85vh]">
        <DialogTitle className="sr-only">Recipe</DialogTitle>
        <DialogDescription className="sr-only">Full-screen recipe detail.</DialogDescription>
        {slot.fullscreenComponentId ? (
          <RecipeDetailPanel
            variant="fullscreen"
            componentId={slot.fullscreenComponentId}
            componentVariantId={slot.fullscreenVariantId}
            onClose={slot.closeFullscreen}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
